<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import DashboardEditor from 'dashboard/components-next/Editor/Editor.vue';
import { ACTION_TYPES } from '../../../constants/nodeTypes.js';
import segmentsAPI from 'escaleshop/segments/api/index.js';

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: [Object, Array, String], default: null },
});
const emit = defineEmits(['update:modelValue']);

const store = useStore();
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');
const slas = useMapGetter('sla/getSLA');

const segments = ref([]);

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('sla/get');
  try {
    const { data } = await segmentsAPI.getAll();
    segments.value = data.segments ?? [];
  } catch {
    segments.value = [];
  }
});

const inputType = computed(
  () => ACTION_TYPES.find(a => a.key === props.actionName)?.inputType ?? null
);

const PRIORITY_OPTIONS = computed(() => [
  { id: 'none', name: t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY_NONE') },
  { id: 'low', name: t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY_LOW') },
  { id: 'medium', name: t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY_MEDIUM') },
  { id: 'high', name: t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY_HIGH') },
  { id: 'urgent', name: t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY_URGENT') },
]);

const dropdownOptions = computed(() => {
  switch (props.actionName) {
    case 'assign_agent':
      return agents.value.map(a => ({ id: a.id, name: a.name, thumbnail: a.thumbnail }));
    case 'assign_team':
    case 'send_email_to_team':
      return teams.value.map(t => ({ id: t.id, name: t.name }));
    case 'change_priority':
      return PRIORITY_OPTIONS.value;
    case 'add_sla':
      return slas.value.map(s => ({ id: s.id, name: s.name }));
    default:
      return [];
  }
});

const labelOptions = computed(() =>
  labels.value.map(l => ({ value: l.title, label: l.title }))
);

const selectedItem = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue[0] ?? null : props.modelValue
);

const multiValue = computed({
  get: () => (Array.isArray(props.modelValue) ? props.modelValue : []),
  set: val => emit('update:modelValue', val),
});

const stringValue = computed({
  get: () => (typeof props.modelValue === 'string' ? props.modelValue : ''),
  set: val => emit('update:modelValue', val),
});

const teamMessageTeam = computed(() => props.modelValue?.team ?? null);
const teamMessageText = computed(() => props.modelValue?.message ?? '');

const fieldLabel = computed(() => {
  if (props.actionName === 'assign_agent') return t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.AGENT');
  if (props.actionName === 'change_priority') return t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.PRIORITY');
  if (props.actionName === 'add_sla') return t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SLA_POLICY');
  return t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.TEAM');
});

const segmentOptions = computed(() =>
  segments.value.map(s => ({ id: s.id, name: s.name }))
);

const selectedSegment = computed(() => {
  const id = props.modelValue?.segment_id ?? props.modelValue?.id ?? null;
  return segmentOptions.value.find(s => s.id === id) ?? null;
});
</script>

<template>
  <div class="flex flex-col gap-3">

    <!-- search_select -->
    <div v-if="inputType === 'search_select'" class="multiselect-wrap--small">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ fieldLabel }}</span>
      </div>
      <MultiselectDropdown
        :options="dropdownOptions"
        :selected-item="selectedItem"
        :multiselector-placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SELECT_FIELD', { field: fieldLabel.toLowerCase() })"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="actionName === 'assign_agent'"
        @select="val => emit('update:modelValue', val)"
      />
    </div>

    <!-- multi_select (labels) -->
    <div v-else-if="inputType === 'multi_select'" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.LABELS') }}</span>
      <TagMultiSelectComboBox
        v-model="multiValue"
        :options="labelOptions"
        :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SELECT_LABELS')"
        :search-placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SEARCH_LABEL')"
        :empty-state="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.NO_LABELS')"
      />
    </div>

    <!-- segment_select -->
    <div v-else-if="inputType === 'segment_select'" class="multiselect-wrap--small">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SEGMENT') }}</span>
      </div>
      <MultiselectDropdown
        :options="segmentOptions"
        :selected-item="selectedSegment"
        :multiselector-placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SELECT_SEGMENT')"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="val => emit('update:modelValue', val ? { segment_id: val.id, name: val.name } : null)"
      />
    </div>

    <!-- url -->
    <div v-else-if="inputType === 'url'" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.WEBHOOK_URL') }}</span>
      <Input v-model="stringValue" type="url" placeholder="https://..." />
    </div>

    <!-- textarea: nota privada / nota no contato -->
    <div v-else-if="inputType === 'textarea'" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.NOTE') }}</span>
      <DashboardEditor
        :model-value="stringValue"
        :editor-key="`flow-action-${actionName}`"
        :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.NOTE_PLACEHOLDER')"
        :max-length="10000"
        :show-character-count="false"
        :enable-canned-responses="false"
        :enable-line-breaks="true"
        @update:model-value="stringValue = $event"
      />
    </div>

    <!-- datetime (snooze) -->
    <div v-else-if="inputType === 'datetime'" class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SNOOZE_UNTIL') }}</span>
      <input
        type="datetime-local"
        class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
        :value="stringValue"
        @input="stringValue = $event.target.value"
      />
    </div>

    <!-- team_message: e-mail para equipe -->
    <template v-else-if="inputType === 'team_message'">
      <div class="multiselect-wrap--small">
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.TEAM') }}</span>
        </div>
        <MultiselectDropdown
          :options="dropdownOptions"
          :selected-item="teamMessageTeam"
          :multiselector-placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.SELECT_TEAM')"
          :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
          :has-thumbnail="false"
          @select="val => emit('update:modelValue', { ...(modelValue ?? {}), team: val })"
        />
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.MESSAGE') }}</span>
        <DashboardEditor
          :model-value="teamMessageText"
          editor-key="flow-action-email-team"
          :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SIMPLE_ACTION.TEAM_MESSAGE_PLACEHOLDER')"
          :max-length="10000"
          :show-character-count="false"
          :enable-canned-responses="false"
          :enable-line-breaks="true"
          channel-type="email"
          medium="email"
          @update:model-value="val => emit('update:modelValue', { ...(modelValue ?? {}), message: val })"
        />
      </div>
    </template>

  </div>
</template>
